import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { LOGOS } from '../constants/assets';

const whyWorkData = [
  { 
    icon: "TrendingUp", 
    title: "Growth", 
    desc: "Clear career progression and development programs",
    color: "bg-blue-50 text-[#11469A]"
  },
  { 
    icon: "Users", 
    title: "Culture", 
    desc: "Collaborative, inclusive, and innovative work environment",
    color: "bg-cyan-50 text-[#00AEEF]"
  },
  { 
    icon: "DollarSign", 
    title: "Compensation", 
    desc: "Competitive salary packages and comprehensive benefits",
    color: "bg-emerald-50 text-emerald-600"
  },
  { 
    icon: "Globe", 
    title: "Impact", 
    desc: "Meaningful work that shapes industries across Ghana",
    color: "bg-purple-50 text-purple-600"
  }
];

const categoriesData = [
  "Technology", "Healthcare", "Finance", "Education", "Engineering",
  "Marketing", "Sales", "Administration", "Construction", "Agriculture"
];

const latestJobsData = [
  {
    title: "Executive Personal Assistant to the CEO",
    type: "Full-time",
    company: "RMGhana",
    department: "Administration",
    location: "Accra, Ghana",
    salary: "GHS 5,000 - 8,000",
    deadline: "3/23/2026"
  },
  {
    title: "Senior Operations Manager",
    type: "Full-time",
    company: "RMGhana",
    department: "Logistics",
    location: "Kumasi, Ghana",
    salary: "GHS 8,000 - 12,000",
    deadline: "4/15/2026"
  },
  {
    title: "Frontend Developer (React)",
    type: "Full-time",
    company: "RMGhana",
    department: "Engineering",
    location: "Remote",
    salary: "GHS 7,000 - 10,000",
    deadline: "3/30/2026"
  },
  {
    title: "Sales Executive",
    type: "Full-time",
    company: "RMGhana",
    department: "Marketing",
    location: "Takoradi, Ghana",
    salary: "GHS 3,000 - 5,000",
    deadline: "5/10/2026"
  },
  {
    title: "HR Specialist",
    type: "Full-time",
    company: "RMGhana",
    department: "Human Resources",
    location: "Accra, Ghana",
    salary: "GHS 4,500 - 7,500",
    deadline: "3/28/2026"
  },
  {
    title: "Site Supervisor",
    type: "Full-time",
    company: "RMGhana",
    department: "Construction",
    location: "Tema, Ghana",
    salary: "GHS 6,000 - 9,000",
    deadline: "4/05/2026"
  }
];

const heroImages = [
  "https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/fba485a13b010b4d3d1eaf7cf7404cf3.webp",
  "https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/b4e7fe8bc9ad17da7ebb539695305e91.webp",
  "https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/16a57bbf66d39bed6596b4ae17a431ad.webp",
  "https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/143ffe7bd15c0d686e8f1b198bc5e7f2.webp",
  "https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/1067fc9a8bd44f184d0db7bdb11f5bcc.webp"
];

const Landing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-[100] bg-white sm:bg-white/90 sm:backdrop-blur-md border-b border-gray-100 h-16 sm:h-20 flex items-center px-4 sm:px-12">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
          <img src={LOGOS.dark} alt="RM Ghana" className="h-8 sm:h-10 w-auto object-contain" />
          
          <div className="hidden md:flex items-center gap-8 font-semibold text-sm text-gray-600">
            <a href="#about" className="hover:text-[#11469A] transition-colors">About</a>
            <a href="#services" className="hover:text-[#11469A] transition-colors">Services</a>
            <a href="#jobs" className="hover:text-[#11469A] transition-colors">Jobs</a>
            <Link to="/login" className="text-[#11469A] hover:opacity-80 transition-opacity">Sign In</Link>
            <Link to="/signup" className="bg-[#11469A] text-white px-6 py-2.5 rounded-full hover:bg-[#00AEEF] transition-all shadow-md">
              Register
            </Link>
          </div>

          <button 
            className="md:hidden p-2 text-gray-600 hover:text-[#11469A] transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            <SafeIcon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80] md:hidden"
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[80%] max-w-sm bg-white z-[90] p-8 md:hidden flex flex-col pt-24 shadow-2xl"
            >
              <div className="flex flex-col gap-8">
                <a href="#about" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Info" size={20} className="text-[#00AEEF]" />
                  About RM Ghana
                </a>
                <a href="#services" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Grid" size={20} className="text-[#00AEEF]" />
                  Our Services
                </a>
                <a href="#jobs" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Briefcase" size={20} className="text-[#00AEEF]" />
                  Job Board
                </a>
                <div className="h-px bg-gray-100 my-4"></div>
                <Link to="/login" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-[#11469A] flex items-center gap-3">
                  <SafeIcon name="LogIn" size={20} />
                  Sign In
                </Link>
                <Link to="/signup" onClick={() => setIsMenuOpen(false)} className="bg-[#11469A] text-white px-6 py-4 rounded-xl font-bold text-center shadow-lg">
                  Register Now
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-12 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp} className="text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-[#11469A] text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-6">
              🇬🇭 We're Hiring — 6 Open Positions
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#11469A] leading-tight mb-6">
              Build Your Career with <span className="text-[#00AEEF]">RMGhana.</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Join a dynamic team making impact across Ghana. Explore our open positions and find the role that fits your expertise and ambitions.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start">
              <Link to="/signup" className="px-8 py-4 bg-[#11469A] text-white rounded-xl font-bold hover:bg-[#0d3678] transition-all shadow-lg text-center">
                Get Started Now
              </Link>
              <Link to="/login" className="px-8 py-4 bg-white text-[#11469A] border-2 border-[#11469A] rounded-xl font-bold hover:bg-gray-50 transition-all text-center">
                Client Portal
              </Link>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.8 }} 
            className="relative hidden sm:block lg:block h-[450px] w-full rounded-3xl shadow-2xl overflow-hidden z-10 bg-gray-100"
          >
            <AnimatePresence mode="popLayout">
              <motion.img 
                key={currentHeroIndex}
                src={heroImages[currentHeroIndex]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-cover" 
                alt={`RM Ghana Career Slide ${currentHeroIndex + 1}`} 
              />
            </AnimatePresence>
            
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {heroImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentHeroIndex(idx)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    currentHeroIndex === idx ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/90'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="relative z-30 px-4 sm:px-12">
        <div className="max-w-3xl mx-auto -mb-10 sm:-mb-12">
          <motion.div className="bg-white rounded-[2rem] sm:rounded-full shadow-xl p-1.5 flex flex-col sm:flex-row border-[6px] border-gray-200 items-stretch">
            <div className="flex-1 flex items-center px-4 sm:px-8 py-2 sm:py-0">
              <SafeIcon name="Search" className="text-gray-400 mr-3" size={20} />
              <input 
                type="text" 
                placeholder="Search by role, department or location" 
                className="w-full bg-transparent outline-none text-gray-700 placeholder-gray-500 font-medium py-2 sm:py-4 text-sm sm:text-base text-center sm:text-left" 
              />
            </div>
            <button className="bg-[#294B98] hover:bg-[#1a3670] text-white px-8 sm:px-12 py-3.5 sm:py-4 rounded-[1.5rem] sm:rounded-r-full sm:rounded-l-none font-bold transition-all shadow-sm">
              Search
            </button>
          </motion.div>
        </div>
      </div>

      {/* Why Work Section (Brand Blue Bg, White Title) */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-20 bg-[#11469A] px-4 sm:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-10 sm:mb-16">Why Work at RMGhana?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyWorkData.map((item, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl border border-gray-100 text-center shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] hover:shadow-xl transition-all group"
              >
                <div className={`w-16 h-16 mx-auto rounded-2xl ${item.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon name={item.icon} size={28} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by Category (Cyan Blue Bg) */}
      <section className="py-16 sm:py-20 bg-[#00AEEF] px-4 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-10">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
            {categoriesData.map((cat, idx) => (
              <Link to="/jobs" key={idx} className="px-4 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-[13px] font-bold text-white transition-all text-center backdrop-blur-sm">
                {cat}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Openings (Updated Styling & 6 Cards) */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Latest Openings</h2>
            <Link to="/jobs" className="text-[#00AEEF] font-bold text-sm hover:underline flex items-center gap-1">
              View All <SafeIcon name="ArrowRight" size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestJobsData.map((job, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-[15px] font-bold text-gray-900 line-clamp-1 flex-1 pr-2">{job.title}</h3>
                  <span className="bg-[#E0F6FF] text-[#00AEEF] text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap">{job.type}</span>
                </div>
                <p className="text-[13px] text-gray-500 mb-4 font-medium">{job.company} · {job.department}</p>
                <div className="flex flex-wrap items-center gap-3 mb-5 mt-auto text-[12px] text-gray-500">
                  <span className="flex items-center gap-1 font-medium"><SafeIcon name="MapPin" size={12} /> {job.location}</span>
                  <span className="font-bold text-[#00AEEF]">{job.salary}</span>
                </div>
                <p className="text-[11px] text-gray-400 font-medium">Deadline: {job.deadline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-24 bg-[#e0f6ff] px-4 sm:px-12 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-[32px] font-bold text-[#0351A4] mb-3">Ready to Join Our Team?</h2>
          <p className="text-[15px] sm:text-lg text-[#0351A4]/80 mb-10 font-medium">Create your profile, upload your resume, and apply to open positions today.</p>
          <Link to="/signup" className="inline-block px-10 py-4 bg-[#0351A4] text-white rounded-lg font-bold hover:bg-[#023e7d] transition-all shadow-lg hover:scale-105 transform duration-300">
            Get Started
          </Link>
        </div>
      </section>

      {/* Footer (Refined with Powered by Link) */}
      <footer className="py-12 bg-[#2F3349] text-gray-400 px-4 sm:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <img src={LOGOS.dark} alt="RM Ghana" className="h-8 brightness-0 invert opacity-50" />
          <p className="text-sm font-medium text-center">
            © 2026 Resources & Manpower Ghana. All rights reserved. | Powered by <a href="https://sanbramedia.com" target="_blank" rel="noopener noreferrer" className="text-[#00AEEF] hover:text-white transition-colors font-bold">RMG Digital</a>
          </p>
          <div className="flex gap-6">
            <SafeIcon name="Linkedin" size={20} className="hover:text-white cursor-pointer transition-colors" />
            <SafeIcon name="Twitter" size={20} className="hover:text-white cursor-pointer transition-colors" />
            <SafeIcon name="Facebook" size={20} className="hover:text-white cursor-pointer transition-colors" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;